import { useMemo } from "react";
import { Bar } from "@ant-design/plots";

type AnyProps = Record<string, any>;
type ValueFormatter = (value: number | null) => string;

export const BarChart = ({
  dataset = [],
  yAxis = [],
  series = [],
  width = "100%",
  height = 300,
  paddingLeft = 110,
}: AnyProps) => {
  const yDataKey = yAxis?.[0]?.dataKey ?? "label";
  const xDataKey = series?.[0]?.dataKey ?? "value";
  const color = series?.[0]?.color ?? "#1677ff";
  const valueFormatter = series?.[0]?.valueFormatter as ValueFormatter | undefined;
  const leftPadding = Number.isFinite(paddingLeft) ? Number(paddingLeft) : 110;
  const safeLeftPadding = Math.max(80, Math.min(260, leftPadding));
  const fallbackValueFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
      }),
    []
  );

  const data = useMemo(
    () =>
      dataset.map((row: AnyProps, index: number) => ({
        key: `${row?.[yDataKey] ?? "row"}-${index}`,
        label: String(row?.[yDataKey] ?? ""),
        value: Number(row?.[xDataKey] ?? 0),
      })),
    [dataset, xDataKey, yDataKey]
  );

  const config = useMemo(
    () => {
      const getFormattedValue = (value: number) =>
        valueFormatter ? valueFormatter(value) : fallbackValueFormatter.format(value);

      return {
        data,
        xField: "label",
        yField: "value",
        color,
        autoFit: true,
        height,
        legend: false,
        padding: [16, 20, 16, safeLeftPadding] as [number, number, number, number],
        barStyle: {
          radius: [0, 8, 8, 0],
        },
        xAxis: {
          label: {
            autoHide: false,
            autoRotate: false,
            style: {
              fill: "#2b4368",
              fontSize: 12,
              fontWeight: 500,
            },
          },
        },
        yAxis: {
          label: {
            formatter: (rawValue: string) => {
              const parsedValue = Number(rawValue);
              if (Number.isNaN(parsedValue)) {
                return rawValue;
              }
              return getFormattedValue(parsedValue);
            },
            style: {
              fill: "#6b83a7",
              fontSize: 12,
            },
          },
          grid: {
            line: {
              style: {
                stroke: "#e8f0fb",
                lineDash: [4, 4],
              },
            },
          },
        },
        label: false,
        tooltip: {
          showTitle: false,
          formatter: (datum: AnyProps) => {
            const value = Number(datum?.value ?? 0);
            return {
              name: datum?.label ?? "",
              value: getFormattedValue(value),
            };
          },
        },
        interactions: [{ type: "element-active" }],
      };
    },
    [
      color,
      data,
      fallbackValueFormatter,
      height,
      safeLeftPadding,
      valueFormatter,
    ]
  );

  return (
    <div style={{ width, minHeight: height }}>
      <Bar {...(config as AnyProps)} />
    </div>
  );
};

export default BarChart;
